import React from 'react';

import { useIntl, defineMessages } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap, cx } from '@atlaskit/css';
import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type HierarchyUser } from './types';

const MAX_PREVIEW_AVATARS = 3;

const styles = cssMap({
	row: {
		width: '285px',
		maxWidth: '100%',
	},
	pressable: {
		width: '285px',
		maxWidth: '100%',
		backgroundColor: token('color.background.neutral.subtle'),
		padding: 0,
		border: 'none',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		transition: token('motion.button.hovered'),
	},
	avatarStack: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
	},
	avatarOverlap: {
		marginInlineStart: token('space.negative.075'),
	},
});

const messages = defineMessages({
	moreReports: {
		id: 'reporting-lines.hierarchy-chart.-more-reports',
		defaultMessage: '{count} more',
		description:
			'Label on the overflow card when there are more direct reports than can be shown in the grid',
	},
});

interface HierarchyReportsOverflowCardProps {
	hiddenReportsPreview: HierarchyUser[];
	hiddenCount: number;
	testId?: string;
	onClick?: () => void;
	centered?: boolean;
}

/**
 * Static overflow tile: overlapping avatars from hidden reports plus “See N more” copy.
 */
export function HierarchyReportsOverflowCard({
	hiddenReportsPreview,
	hiddenCount,
	testId,
	onClick,
	centered,
}: HierarchyReportsOverflowCardProps): React.JSX.Element {
	const intl = useIntl();
	const preview = hiddenReportsPreview.slice(0, MAX_PREVIEW_AVATARS);

	const content = (
		<Inline
			xcss={styles.row}
			testId={testId}
			alignBlock="center"
			alignInline={centered ? 'center' : 'start'}
			space="space.150"
		>
			<Box xcss={styles.avatarStack}>
				{preview.map((user, index) => (
					<Box key={user.accountId} xcss={cx(index > 0 && styles.avatarOverlap)}>
						<Avatar
							size="medium"
							name={user.name}
							src={user.avatarUrl ?? undefined}
							label={user.name}
						/>
					</Box>
				))}
			</Box>
			<Text>
				{intl.formatMessage(messages.moreReports, {
					count: hiddenCount,
				})}
			</Text>
		</Inline>
	);

	if (onClick) {
		return (
			<Pressable xcss={styles.pressable} onClick={onClick}>
				{content}
			</Pressable>
		);
	}

	return content;
}
